import React, { useState } from 'react';
import { cancelInvoice } from '../services/invoiceService';
import { formatBs, formatCurrency } from '../utils/format';
import { describePortionChanges } from '../utils/portions';
import { paymentMethodIcon, paymentMethodLabel } from '../utils/payments';

export default function FacturaDetalleModal({ invoice, onClose, onCancelled }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const details = invoice.details || [];
  const payments = invoice.payments || [];
  const client = invoice.client;

  async function handleCancel() {
    setError('');
    setBusy(true);
    try {
      const data = await cancelInvoice(invoice.id);
      onCancelled?.(data.invoice);
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo anular la factura.');
      setBusy(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="section-card"
        style={{ width: '100%', maxWidth: 600, marginBottom: 0, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="section-header">
          <h6>
            <i className="bi bi-receipt" style={{ color: 'var(--orig-green)' }} />
            Factura #{String(invoice.id).padStart(6, '0')}
            {!invoice.state && (
              <span className="badge-orig badge-red" style={{ marginLeft: 8 }}>Anulada</span>
            )}
          </h6>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--orig-text-muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
            title="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="section-body">
          {error && (
            <div className="alert-orig alert-error" style={{ marginBottom: 16 }}>
              <i className="bi bi-exclamation-triangle-fill" />
              {error}
            </div>
          )}

          <div className="row g-3" style={{ marginBottom: 18 }}>
            <div className="col-12 col-md-6">
              <div style={{ fontSize: 11, color: 'var(--orig-text-muted)', marginBottom: 2 }}>Cliente</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--orig-text)' }}>{client?.name}</div>
              <div style={{ fontSize: 12, color: 'var(--orig-text-muted)' }}>
                {client ? `${client.nationality}-${client.document}` : '—'}
                {client?.phone ? ` · ${client.phone}` : ''}
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={{ fontSize: 11, color: 'var(--orig-text-muted)', marginBottom: 2 }}>Fecha</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--orig-text)' }}>
                {invoice.date ? new Date(invoice.date).toLocaleString('es-VE') : '—'}
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div style={{ fontSize: 11, color: 'var(--orig-text-muted)', marginBottom: 2 }}>Stand</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--orig-text)' }}>
                {invoice.mobileStand?.name || '—'}
              </div>
            </div>
            {invoice.user && (
              <div className="col-6 col-md-6">
                <div style={{ fontSize: 11, color: 'var(--orig-text-muted)', marginBottom: 2 }}>Atendido por</div>
                <div style={{ fontSize: 13, color: 'var(--orig-text)' }}>{invoice.user.name} {invoice.user.lastName}</div>
              </div>
            )}
            {invoice.observations && (
              <div className="col-12">
                <div style={{ fontSize: 11, color: 'var(--orig-text-muted)', marginBottom: 2 }}>Observaciones</div>
                <div style={{ fontSize: 13, color: 'var(--orig-text)' }}>{invoice.observations}</div>
              </div>
            )}
          </div>

          <table className="orig-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Producto</th>
                <th style={{ textAlign: 'center' }}>Cant.</th>
                <th style={{ textAlign: 'right' }}>Precio</th>
                <th style={{ textAlign: 'right' }}>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {details.map((d, idx) => {
                const changes = describePortionChanges((d.portionItems || []).map(p => ({
                  name: p.portion?.name, quantity: p.quantity, recipeQuantity: p.recipeQuantity,
                })));
                return (
                <tr key={d.id}>
                  <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>{idx + 1}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{d.menuProduct?.name}</div>
                    {changes.length > 0 && (
                      <div style={{ fontSize: 11, lineHeight: 1.35 }}>
                        {changes.map((c, i) => (
                          <span
                            key={i}
                            style={{ color: c.type === 'extra' ? 'var(--orig-green-bright)' : 'var(--orig-amber-light)', marginRight: 8 }}
                          >
                            {c.text}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>{d.quantity}</td>
                  <td style={{ textAlign: 'right', fontSize: 12.5, color: 'var(--orig-text-muted)' }}>{formatCurrency(d.unitPrice)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(d.subtotal)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--orig-border)',
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--orig-text-muted)' }}>TOTAL</div>
              {invoice.exchangeRate && (
                <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>Tasa: {formatBs(invoice.exchangeRate)} / $</div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--orig-green-bright)', fontFamily: 'var(--orig-font-display)', lineHeight: 1.1 }}>
                {formatCurrency(invoice.totalUsd)}
              </div>
              {Number(invoice.totalBs) > 0 && (
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--orig-text)' }}>{formatBs(invoice.totalBs)}</div>
              )}
            </div>
          </div>

          {payments.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--orig-text)', marginBottom: 6 }}>Formas de pago</div>
              {payments.map(p => (
                <div
                  key={p.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--orig-border)' }}
                >
                  <i className={`bi ${paymentMethodIcon(p.method)}`} style={{ color: 'var(--orig-green)', fontSize: 16 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: 'var(--orig-text)' }}>{paymentMethodLabel(p.method)}</div>
                    {p.reference && (
                      <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>Ref: {p.reference}</div>
                    )}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--orig-text)' }}>
                    {Number(p.amountBs) > 0 ? formatBs(p.amountBs) : formatCurrency(p.amountUsd)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {invoice.state && onCancelled && (
          <div style={{
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            padding: '14px 20px', borderTop: '1px solid var(--orig-border)',
          }}>
            {confirming ? (
              <>
                <span style={{ fontSize: 12.5, color: 'var(--orig-text-muted)', marginRight: 'auto' }}>
                  ¿Anular esta factura? Las porciones volverán al inventario del stand.
                </span>
                <button type="button" className="btn-orig btn-orig-outline" onClick={() => setConfirming(false)} disabled={busy}>
                  No
                </button>
                <button
                  type="button"
                  className="btn-orig btn-orig-outline"
                  onClick={handleCancel}
                  disabled={busy}
                  style={{ color: 'var(--orig-red-light)', borderColor: 'var(--orig-red)' }}
                >
                  <i className="bi bi-x-circle" />
                  {busy ? 'Anulando...' : 'Sí, anular'}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn-orig btn-orig-outline"
                onClick={() => setConfirming(true)}
                style={{ color: 'var(--orig-red-light)' }}
              >
                <i className="bi bi-x-circle" />
                Anular factura
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
